/**
 * Чистый редьюсер всего потока тренажёра: навигация по блокам, переменные, история.
 *
 * Состояние полностью детерминировано действиями (actions), поэтому одну и ту же
 * логику переходов могут использовать и UI, и будущий AI-контроллер, который будет
 * «вести» пользователя по шагам и давать советы.
 *
 * Shape состояния:
 *   {
 *     config: { blocks, first_phrase_screen, next_phrase_screen } | null,
 *     currentBlockId: string | null,
 *     variables: Object,           // значения ответов (часть — массивы по фразам)
 *     history: Array,              // лента пройденных сообщений
 *     showSummary: boolean,        // показывать экран итогов
 *     viewingPhraseIndex: number,  // какая фраза просматривается (для истории/сайдбара)
 *   }
 */
import { INITIAL_VARIABLES, SUMMARY_TARGET } from '../config/appConfig';
import { setVariableValue, getScalarValue } from '../utils/variables';
import { computeBlockValue } from '../utils/variableValue';
import { renderTemplate } from '../utils/templateEngine';
import { addSystemMessage, addUserMessage, addInputHistory } from '../utils/history';

export const getBlock = (state, blockId) => {
  if (!state.config || !state.config.blocks) return null;
  return state.config.blocks.find((block) => block.id === blockId) || null;
};

// Общий переход: на следующий блок, либо на экран итогов.
const navigate = (state, nextId) => {
  if (!nextId || nextId === SUMMARY_TARGET) {
    return { ...state, showSummary: true };
  }
  return { ...state, currentBlockId: nextId, showSummary: false };
};

const handleBlockNext = (state, { value, nextId, skip }) => {
  const block = getBlock(state, state.currentBlockId);
  if (!block) return state;

  // Пропуск: ничего не сохраняем и не пишем в историю.
  if (skip) {
    return navigate(state, nextId);
  }

  const phraseIndex = state.variables.currentPhraseIndex || 0;
  let variables = state.variables;

  // Сохраняем значение в переменную блока.
  if (block.variable && value !== undefined && value !== null) {
    const existing = getScalarValue(state.variables, block.variable, phraseIndex);
    const newValue = computeBlockValue(block, value, existing);
    variables = setVariableValue(variables, block.variable, newValue, phraseIndex);
  }

  // Доп. переменные блока.
  if (block.setVariable) {
    Object.entries(block.setVariable).forEach(([key, val]) => {
      variables = setVariableValue(variables, key, val);
    });
  }

  // История. Текст рендерим по переменным ДО применения текущего ответа
  // (вопрос блока ссылается на ответы предыдущих шагов).
  const renderedText = renderTemplate(block.text, state.variables);
  let history = state.history;

  if (block.type === 'input') {
    history = addInputHistory(history, renderedText, value, phraseIndex);
  } else if (block.type === 'choice') {
    const selected = (block.buttons || []).find(
      (btn) =>
        btn.value === value ||
        (btn.value !== undefined && String(btn.value) === String(value)) ||
        btn.text === value
    );
    history = addUserMessage(
      addSystemMessage(history, renderedText, phraseIndex),
      renderedText,
      selected ? selected.text : String(value),
      phraseIndex
    );
  } else if (block.type === 'info') {
    history = addSystemMessage(history, renderedText, phraseIndex);
  }
  // condition-блоки в историю не пишутся (они невидимы).

  return navigate({ ...state, variables, history }, nextId);
};

export const appReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CONFIG': {
      const next = { ...state, config: action.config };
      const { blocks, first_phrase_screen: firstScreen } = action.config || {};

      if (!state.currentBlockId && firstScreen) {
        // Новый пользователь — стартуем с первого экрана.
        next.currentBlockId = firstScreen;
      } else if (state.currentBlockId) {
        // Сохранённый экран мог исчезнуть в новой конфигурации.
        const exists = blocks && blocks.some((b) => b.id === state.currentBlockId);
        if (!exists && firstScreen) {
          next.currentBlockId = firstScreen;
        }
      }
      return next;
    }

    case 'BLOCK_NEXT':
      return handleBlockNext(state, action);

    case 'CONDITION_NEXT':
      return navigate(state, action.nextId);

    case 'RESTART': {
      const start = (state.config && state.config.first_phrase_screen) || 'situation';
      return {
        ...state,
        currentBlockId: start,
        history: [],
        showSummary: false,
        viewingPhraseIndex: 0,
        variables: { ...INITIAL_VARIABLES },
      };
    }

    case 'NEXT_PHRASE': {
      const oldIndex = state.variables.currentPhraseIndex || 0;
      const prevStep = getScalarValue(state.variables, 'step', oldIndex); // этап уходящей фразы
      const nextScreen =
        (state.config && state.config.next_phrase_screen) || 'next_phrase_screen1';
      let variables = setVariableValue(state.variables, 'currentPhraseIndex', oldIndex + 1);
      variables = setVariableValue(variables, 'prevStep', prevStep);
      return {
        ...state,
        variables,
        viewingPhraseIndex: oldIndex + 1,
        currentBlockId: nextScreen,
        showSummary: false,
      };
    }

    case 'SELECT_PHRASE':
      return {
        ...state,
        viewingPhraseIndex: action.index,
        variables: setVariableValue(state.variables, 'viewingPhraseIndex', action.index),
      };

    case 'ASK_ASSISTANT':
      // Клик по *слову#id*. Глоссарий уже разрешён в payload:
      // answer — зашитый ответ (показать сразу), question — вопрос к AI. assistantNonce — триггер UI.
      return {
        ...state,
        assistantTerm: action.payload.term,
        assistantAnswer: action.payload.answer || null,
        assistantQuestion: action.payload.question || null,
        assistantNonce: (state.assistantNonce || 0) + 1,
      };

    default:
      return state;
  }
};
